import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import notificationsApi from '@/consumer/lib/notifications-api'
import type {
  NotificationListParams,
  NotificationRow,
} from '@/admin/hooks/use-notifications'

/**
 * Notification infrastructure - consumer data layer.
 *
 * Mirrors the admin `use-notifications` hook but bound to the consumer's
 * notifications client. Every consumer surface (the claims-list attention dot,
 * the claim-detail panel) reads from this hook so read state stays a single,
 * server-side, cross-device fact - never a per-browser localStorage marker.
 */

export type {
  NotificationRow,
  NotificationListParams,
} from '@/admin/hooks/use-notifications'

const NOTIFICATIONS_KEY = 'consumer-notifications'

export function useConsumerNotifications(
  params?: NotificationListParams,
  options?: { enabled?: boolean },
) {
  return useQuery<{ results: NotificationRow[]; count: number }>({
    queryKey: [NOTIFICATIONS_KEY, params ?? {}],
    queryFn: () => notificationsApi.get('/', { params }).then((r) => r.data),
    enabled: options?.enabled ?? true,
  })
}

function useInvalidateConsumerNotifications() {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] })
    // The claims list carries the per-card dot; refresh it too.
    qc.invalidateQueries({ queryKey: ['consumer-claims'] })
  }
}

/**
 * Bulk / scoped mark-read. Pass `uuids` to clear specific rows, or
 * `record_type` + `record_uuid` to clear every unread notification for a
 * record.
 */
export function useMarkConsumerNotificationsRead() {
  const invalidate = useInvalidateConsumerNotifications()
  return useMutation<
    { updated: number },
    Error,
    { uuids?: string[]; record_type?: string; record_uuid?: string; category?: string }
  >({
    mutationFn: (body) =>
      notificationsApi.post('/mark-read/', body).then((r) => r.data),
    onSuccess: invalidate,
  })
}

export function useConsumerNotificationItemAction() {
  const invalidate = useInvalidateConsumerNotifications()
  return useMutation<
    unknown,
    Error,
    { uuid: string; action: 'read' | 'unread' | 'archive' }
  >({
    mutationFn: ({ uuid, action }) =>
      notificationsApi.post(`/${uuid}/${action}/`).then((r) => r.data),
    onSuccess: invalidate,
  })
}
